import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Send, ShieldCheck, Info, Home as HomeIcon } from "lucide-react";
import Logo from "./Logo";
import NewsletterSubscribe from "./NewsletterSubscribe";
import { api } from "../lib/api";

export default function Footer() {
  const { data: pubSettings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => api.get("/public-settings").then((r) => r.data),
    staleTime: 60_000,
  });
  const channel = pubSettings?.telegram_channel || "@BrickDailyDeals";

  const handle = (channel || "").replace("@", "");

  return (
    <footer className="mt-24 border-t border-zinc-900 bg-zinc-950" data-testid="site-footer">
      {/* big telegram CTA */}
      <div className="relative overflow-hidden border-b border-zinc-900">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 py-20 text-center relative">
          <div className="font-mono-bdd text-xs uppercase tracking-[0.35em] text-[#FBD51C] mb-4">
            Canale Telegram
          </div>
          <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-[0.95] mb-4">
            Le migliori offerte
            <br />
            <span className="text-gold-grad">ogni giorno, sul tuo telefono.</span>
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto mb-8">
            Iscriviti gratis al canale {channel} e ricevi notifiche istantanee
            su sconti, lampo e novità sui migliori set di costruzioni.
          </p>
          <a
            href={`https://t.me/${handle}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-telegram-cta"
            className="inline-flex items-center gap-2 bg-[#229ED9] hover:bg-[#1c8acb] text-white font-bold text-base rounded-full px-7 py-4 transition-all hover:scale-[1.02]"
          >
            <Send size={18} />
            Unisciti a {channel}
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Logo />
          <p className="text-sm text-zinc-500 mt-4 max-w-sm">
            Selezione quotidiana delle migliori offerte sui set di costruzioni
            dai principali store online. Curato da appassionati, per appassionati.
          </p>
          {/* Social presence — required by AdSense */}
          <div className="mt-5">
            <div className="font-mono-bdd text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
              Seguici
            </div>
            <div className="flex items-center gap-2">
              <a
                href={`https://t.me/${handle}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-telegram"
                aria-label={`Canale Telegram ${channel}`}
                title={`Canale Telegram ${channel}`}
                className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#229ED9] hover:bg-[#1c8acb] text-white transition-colors"
              >
                <Send size={16} />
              </a>
            </div>
          </div>

          <div className="mt-6">
            <NewsletterSubscribe variant="footer" source="footer" />
          </div>
        </div>

        <div>
          <div className="font-mono-bdd text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">
            Store
          </div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/?store=amazon" className="text-zinc-300 hover:text-[#FBD51C]">Amazon</Link></li>
            <li><Link to="/?store=aliexpress" className="text-zinc-300 hover:text-[#FBD51C]">AliExpress</Link></li>
            <li><Link to="/?store=ebay" className="text-zinc-300 hover:text-[#FBD51C]">eBay</Link></li>
          </ul>
        </div>

        <div>
          <div className="font-mono-bdd text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">
            Info
          </div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" data-testid="footer-home" className="text-zinc-300 hover:text-[#FBD51C] inline-flex items-center gap-1.5"><HomeIcon size={12}/> Home</Link></li>
            <li><a href={`https://t.me/${handle}`} target="_blank" rel="noopener noreferrer" className="text-zinc-300 hover:text-[#FBD51C]">Canale Telegram</a></li>
            <li><Link to="/guide" data-testid="footer-guide" className="text-zinc-300 hover:text-[#FBD51C]">Guide e News</Link></li>
            <li><Link to="/come-valutiamo" data-testid="footer-metodologia" className="text-zinc-300 hover:text-[#FBD51C]">Come valutiamo</Link></li>
            <li><Link to="/chi-siamo" data-testid="footer-chi-siamo" className="text-zinc-300 hover:text-[#FBD51C]">Chi siamo</Link></li>
            <li><Link to="/contatti" data-testid="footer-contatti" className="text-zinc-300 hover:text-[#FBD51C]">Contatti</Link></li>
            <li><Link to="/termini" data-testid="footer-termini" className="text-zinc-300 hover:text-[#FBD51C]">Termini e Condizioni</Link></li>
            <li><Link to="/privacy" data-testid="footer-privacy" className="text-zinc-300 hover:text-[#FBD51C]">Privacy Policy</Link></li>
            <li><Link to="/cookie" data-testid="footer-cookie" className="text-zinc-300 hover:text-[#FBD51C]">Cookie Policy</Link></li>
            <li><Link to="/admin/login" data-testid="footer-admin-link" className="text-zinc-300 hover:text-[#FBD51C]">Area Admin</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-6 text-xs text-zinc-500 flex flex-col md:flex-row items-start md:items-center gap-3 justify-between">
          <div className="flex items-start gap-2 max-w-2xl">
            <Info size={14} className="text-zinc-600 mt-0.5 shrink-0" />
            <span>
              Canale indipendente non affiliato né sponsorizzato da The LEGO Group.
              Tutti i marchi citati appartengono ai rispettivi proprietari.
              Alcuni link possono essere affiliati: acquistando tramite essi
              sostieni il progetto senza costi aggiuntivi.
            </span>
          </div>
          <div className="flex items-center gap-2 text-zinc-600">
            <ShieldCheck size={14} />
            <span>© {new Date().getFullYear()} BrickDailyDeals</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
